<script lang="ts" module>
  export interface ComboOption {
    value: number | string;
    label: string;
    sublabel?: string;
    color?: string;
  }
</script>

<script lang="ts">
  import { ChevronDown, Plus, X } from '@lucide/svelte';
  import Popover from './Popover.svelte';
  import Spinner from './Spinner.svelte';
  import ColorDot from './ColorDot.svelte';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    options: ComboOption[];
    value?: number | string | null;
    placeholder?: string;
    disabled?: boolean;
    clearable?: boolean;
    invalid?: boolean;
    /** Show "+ create '<query>'" when nothing matches exactly. */
    allowCreate?: boolean;
    createLabel?: (query: string) => string;
    oncreate?: (query: string) => Promise<number | string | null | void>;
    onchange?: (value: number | string | null, option: ComboOption | null) => void;
    emptyLabel?: string;
    id?: string;
    autofocus?: boolean;
  }
  let {
    options,
    value = $bindable(null),
    placeholder = '',
    disabled = false,
    clearable = false,
    invalid = false,
    allowCreate = false,
    createLabel = (q) => t('ui.create_option', { name: q }),
    oncreate,
    onchange,
    emptyLabel,
    id,
    autofocus = false
  }: Props = $props();

  const listId = `cb-${Math.random().toString(36).slice(2, 8)}`;
  let wrapper: HTMLElement | null = $state(null);
  let input: HTMLInputElement | null = $state(null);
  let open = $state(false);
  let query = $state('');
  let typing = $state(false);
  let highlighted = $state(0);
  let creating = $state(false);

  const selected = $derived(options.find((o) => o.value === value) ?? null);
  const display = $derived(typing ? query : selected?.label ?? '');

  const filtered = $derived.by(() => {
    const q = (typing ? query : '').trim().toLocaleLowerCase();
    if (!q) return options;
    const scored: Array<{ o: ComboOption; s: number }> = [];
    for (const o of options) {
      const label = o.label.toLocaleLowerCase();
      const sub = (o.sublabel || '').toLocaleLowerCase();
      let s = -1;
      if (label === q) s = 0;
      else if (label.startsWith(q)) s = 1;
      else if (label.split(/\s+/).some((w) => w.startsWith(q))) s = 2;
      else if (label.includes(q)) s = 3;
      else if (sub.includes(q)) s = 4;
      if (s >= 0) scored.push({ o, s });
    }
    return scored.sort((a, b) => a.s - b.s || a.o.label.localeCompare(b.o.label)).map((x) => x.o);
  });
  const exact = $derived(filtered.some((o) => o.label.toLocaleLowerCase() === query.trim().toLocaleLowerCase()));
  const showCreate = $derived(allowCreate && typing && query.trim().length > 0 && !exact);
  const total = $derived(filtered.length + (showCreate ? 1 : 0));

  function openList() {
    if (disabled) return;
    open = true;
    highlighted = Math.max(0, filtered.findIndex((o) => o.value === value));
  }

  function pick(option: ComboOption | null) {
    value = option ? option.value : null;
    typing = false;
    query = '';
    open = false;
    onchange?.(value, option);
  }

  async function create() {
    if (!oncreate || creating) return;
    const name = query.trim();
    if (!name) return;
    creating = true;
    try {
      const id = await oncreate(name);
      if (id !== undefined && id !== null) {
        value = id;
        onchange?.(id, { value: id, label: name });
      }
      typing = false;
      query = '';
      open = false;
    } finally {
      creating = false;
    }
  }

  function onInput(event: Event) {
    query = (event.currentTarget as HTMLInputElement).value;
    typing = true;
    open = true;
    highlighted = 0;
  }

  function onKey(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openList();
      else highlighted = (highlighted + 1) % Math.max(1, total);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlighted = (highlighted - 1 + Math.max(1, total)) % Math.max(1, total);
    } else if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      if (highlighted < filtered.length) pick(filtered[highlighted]);
      else if (showCreate) create();
    } else if (event.key === 'Escape') {
      if (open) {
        event.stopPropagation();
        open = false;
        typing = false;
        query = '';
      }
    } else if (event.key === 'Tab') {
      // Tab out with a half-typed exact match: take it.
      if (typing && filtered.length && filtered[0].label.toLocaleLowerCase() === query.trim().toLocaleLowerCase()) pick(filtered[0]);
      open = false;
    }
  }

  function onBlur() {
    // Leave the text as it was if nothing was chosen.
    setTimeout(() => {
      if (!open) {
        typing = false;
        query = '';
      }
    }, 120);
  }

  $effect(() => {
    if (autofocus && input) input.focus();
  });
</script>

<div class="combo" class:invalid class:disabled class:focused={open} bind:this={wrapper}>
  {#if selected?.color && !typing}<span class="lead-dot"><ColorDot color={selected.color} size="md" /></span>{/if}
  <input
    bind:this={input}
    {id}
    class="inp"
    type="text"
    role="combobox"
    aria-expanded={open}
    aria-controls={listId}
    aria-autocomplete="list"
    autocomplete="off"
    {placeholder}
    {disabled}
    value={display}
    oninput={onInput}
    onfocus={openList}
    onclick={openList}
    onkeydown={onKey}
    onblur={onBlur}
  />
  {#if clearable && value !== null && value !== undefined && !disabled}
    <button type="button" class="icon-btn" aria-label={t('ui.clear')} onclick={() => pick(null)}><X size={14} /></button>
  {/if}
  <button type="button" class="icon-btn" tabindex="-1" aria-label={t('ui.open_list')} {disabled} onclick={() => (open ? (open = false) : (input?.focus(), openList()))}>
    <ChevronDown size={16} />
  </button>
</div>

<Popover bind:open anchor={wrapper} matchWidth placement="bottom-start" onclose={() => { typing = false; query = ''; }}>
  <div class="list" role="listbox" id={listId}>
    {#each filtered as option, i (option.value)}
      <button
        type="button"
        role="option"
        aria-selected={option.value === value}
        class="opt"
        class:hl={i === highlighted}
        class:sel={option.value === value}
        onpointerenter={() => (highlighted = i)}
        onpointerdown={(e) => e.preventDefault()}
        onclick={() => pick(option)}
      >
        {#if option.color}<ColorDot color={option.color} size="md" />{/if}
        <span class="txt">
          <span class="lbl">{option.label}</span>
          {#if option.sublabel}<span class="sub">{option.sublabel}</span>{/if}
        </span>
      </button>
    {/each}
    {#if showCreate}
      <button type="button" class="opt create" class:hl={highlighted === filtered.length} onpointerdown={(e) => e.preventDefault()} onclick={create}>
        {#if creating}<Spinner size={14} />{:else}<Plus size={15} />{/if}
        <span class="lbl">{createLabel(query.trim())}</span>
      </button>
    {/if}
    {#if !filtered.length && !showCreate}
      <div class="empty">{emptyLabel ?? t('ui.no_results')}</div>
    {/if}
  </div>
</Popover>

<style>
  .combo {
    display: flex; align-items: center;
    height: var(--control-h);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
    transition: border-color var(--dur), box-shadow var(--dur);
    min-width: 0;
  }
  .combo:hover { border-color: var(--border-strong); }
  .combo:focus-within, .combo.focused { border-color: var(--brand); box-shadow: var(--ring); }
  .combo.invalid { border-color: var(--danger); }
  .combo.disabled { opacity: 0.6; background: var(--surface-2); }
  .inp { flex: 1; min-width: 0; height: 100%; border: 0; background: transparent; padding: 0 var(--space-1) 0 var(--space-3); font-size: var(--text-base); outline: none; color: var(--text); }
  .inp::placeholder { color: var(--text-3); }
  .lead-dot { display: inline-flex; margin-left: var(--space-3); margin-right: calc(var(--space-1) * -1); }
  .icon-btn { border: 0; background: transparent; color: var(--text-3); width: 30px; height: 100%; display: grid; place-items: center; flex-shrink: 0; border-radius: var(--radius-xs); }
  .icon-btn :global(svg) { width: var(--icon-md); height: var(--icon-md); }
  .icon-btn:focus-visible { outline: none; box-shadow: var(--ring); }
  .icon-btn:hover { color: var(--text); }
  .list { padding: var(--space-1); display: flex; flex-direction: column; }
  .opt {
    display: flex; align-items: center; gap: var(--space-2_5);
    padding: var(--space-2) var(--space-2_5); border: 0; border-radius: var(--radius-xs);
    background: transparent; color: var(--text); text-align: left; font-size: var(--text-base); width: 100%;
  }
  .opt.hl { background: var(--surface-hover); }
  .opt.sel .lbl { font-weight: var(--weight-semibold); color: var(--brand); }
  .opt.create { color: var(--brand); font-weight: var(--weight-semibold); }
  .txt { display: flex; flex-direction: column; min-width: 0; }
  .lbl { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { font-size: var(--text-xs); color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .empty { padding: var(--space-3); color: var(--text-3); font-size: var(--text-sm); text-align: center; }
</style>
